'use client'
import { memo, type RefObject, useMemo, useState, type FC, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { useTranslator } from '@/hooks'
import type { TypeCmsProductDetail } from '@/schema'
import { AnimatePresence, motion } from 'motion/react'
import { createUnderline, designGrid, getP18, getP20_1, getP24_3, mq, vw } from '@/styles'
import { Link } from './commons'
import { downloadBlobFile } from '@/utils'

type Props = {
  data: TypeCmsProductDetail['models']
  ref: RefObject<HTMLElement>
  selectedModel: TypeCmsProductDetail['models'][0]['slug']
}
const getTabPanel = (data: TypeCmsProductDetail['models'], activeModelId: number, title:string, onClickPdf) => {
  const model = data.find(item => item.id === activeModelId)
  if (!model) return null

  return (
    <DivTabPanel
      key={activeModelId}
      role='tabpanel'
      id={`tabpanel-${activeModelId}`}
      aria-labelledby={`tab-${activeModelId}`}
      tabIndex={0}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.dt>{title}</motion.dt>
      {model.downloads.map((item, idx) => (
        <motion.dd key={idx}>
          <button className='btn--pdf' onClick={() => {onClickPdf(item)}}> {item.name}</button>
        </motion.dd>
      ))}
    </DivTabPanel>
  )
}

const headingId = 'downloads'
export const SectionDownloadsPerModel: FC<Props> = memo(({ data, ref, selectedModel }) => {
  const { t } = useTranslator()
  const dataOfSelectedModel = useMemo(() => data.find(item => item.slug === selectedModel), [data, selectedModel])
  const [activeModelId, setActiveModelId] = useState(data[0].id)


  const onClickPdf = useCallback(async (dataFetch) => {
    const { url, filename, mime_type } = dataFetch

		// If mime_type is application/pdf, open the url in a new tab
		if (mime_type === 'application/pdf') {
			const newTab = window.open(url, '_blank')
			if (newTab) newTab.focus()
			return
		}

    try {
      const res = await fetch(url, {
        method: 'GET',
      })

      const data = await res.blob()
      await downloadBlobFile({ blob: data, mimeType: mime_type, name: filename ?? 'nexia_file' })
    } catch (e) {
      console.warn('downloadBlobFile - Catch e', e)
    }
  }, [])

  const TabPanel = useMemo(() => getTabPanel(data, activeModelId, t('project.product.section_downloads.available_downloads') as string, onClickPdf), [data, activeModelId, t, onClickPdf])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, id:number) => {
    const idx = data.findIndex(item => item.id === id)
    if (e.key === 'ArrowRight') {
      if(idx + 1 !== -1 && idx + 1 <= data.length - 1) setActiveModelId(data[idx + 1].id)
    } else if (e.key === 'ArrowLeft') {
      if (idx - 1 !== -1 && idx - 1 <= data.length - 1) setActiveModelId(data[idx -1].id)
    }
  }

  useEffect(() => {
    if (dataOfSelectedModel) {
      queueMicrotask(() => setActiveModelId(dataOfSelectedModel.id))
    }
  }, [dataOfSelectedModel])


  return (
    <Section ref={ref} aria-labelledby={headingId}>
      <h2 id={headingId}>{t('project.product.section_downloads.title')}</h2>
      <dl>
        <DivTabList role='tablist' aria-orientation='vertical'>
          <dt>{t('project.product.section_downloads.single_model') as string}</dt>
          {dataOfSelectedModel &&<dd key={`model-${dataOfSelectedModel.id}`}>
            <button
              key={dataOfSelectedModel.id}
              role='tab'
              id={`tab-${dataOfSelectedModel.id}`}
              aria-selected={activeModelId === dataOfSelectedModel.id}
              aria-controls={`tabpanel-${dataOfSelectedModel.slug}`}
              tabIndex={activeModelId === dataOfSelectedModel.id ? 0 : -1}
              onClick={() => setActiveModelId(dataOfSelectedModel.id)}
              onKeyDown={(e) => handleKeyDown(e, dataOfSelectedModel.id)}
            >
              {dataOfSelectedModel.name}
            </button>
          </dd>}
        </DivTabList>
        <AnimatePresence mode='wait'>
          {TabPanel}
        </AnimatePresence>
      </dl>
      <Link className='l--downloads' href='/downloads'>{t('project.product.section_downloads.all_downloads')}</Link>
    </Section>
  )
})

const Section = styled.section`
  background-color: var(--color-creme);
  ${designGrid({})}
  padding: 90px 0 40px;

  ${mq.greaterThan('desktop')} {
    padding: 90px 0 40px;
  }

  h2 {
    ${getP24_3()}
    font-weight: bold;
    margin-bottom: ${vw(20, 'mobile')};
    text-align: left;

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(20, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      margin-bottom: ${vw(30, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-bottom: 30px;
    }
  }

  h2,
  dl,
  .l--downloads {
    grid-column: 1 / span 6;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
    }
  }

  .l--downloads {
    ${createUnderline({ reverse: true })}
    margin-left: auto;
  }

  dl {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    margin-bottom: 20px;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 8;
      flex-direction: row;
    }

    div {
      &:first-child {
        ${mq.greaterThan('tablet')} {
          padding-right: 15px;
          width: 30%;
        }
      }

      &:last-child {
        ${mq.lessThan('tablet')} {
          margin-top: 30px;
        }

        ${mq.greaterThan('tablet')} {
          padding-left: 15px;
          width: 70%;
        }
      }
    }

    dt {
      ${getP20_1()}
      border-bottom: 1px solid var(--color-text);
      margin-bottom: 10px;
      opacity: .8;
      padding-bottom: 5px;
      width: 100%;
    }
  }
`
const DivTabList = styled(motion.div)`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 5px;
  justify-content: flex-start;

  button {
    ${getP18()}
    opacity: .7;
    transition: 250ms opacity ease-in-out;

    &:hover {
      opacity: 1;
    }

    &[aria-selected='true'] {
      font-weight: bold;
      opacity: 1;
    }
  }
`

const DivTabPanel = styled(motion.div)`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 5px;
  justify-content: flex-start;

  a, button {
    ${getP18()}
    ${createUnderline({ reverse: true})}
  }
`
